import React, { useState } from 'react';
import { Bell, CheckCircle, MessageCircle, Heart, Calendar, ShoppingBag, User, Search, X } from 'lucide-react';
import { useNotifications, Notification } from '../../hooks/useNotifications';
import { showToast } from '../../utils/toast';

interface NotificationsPanelProps {
  onNavigate: (section: string, data?: any) => void;
}

const NotificationsPanel: React.FC<NotificationsPanelProps> = ({ onNavigate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  
  const { notifications, loading, markAsRead, markAllAsRead } = useNotifications();

  // Filter notifications based on search term and active filter
  const filteredNotifications = notifications.filter(notification => {
    const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         notification.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = !activeFilter || notification.type === activeFilter;
    
    return matchesSearch && matchesFilter;
  });

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_message': return <MessageCircle className="text-blue-500" size={18} />;
      case 'post_reaction': return <Heart className="text-red-500" size={18} />;
      case 'expert_answer': return <CheckCircle className="text-green-500" size={18} />;
      case 'new_follower': return <User className="text-purple-500" size={18} />;
      case 'session_reminder': return <Calendar className="text-orange-500" size={18} />;
      case 'order_update': return <ShoppingBag className="text-indigo-500" size={18} />;
      default: return <Bell className="text-gray-500" size={18} />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'new_message': return 'bg-blue-50 dark:bg-blue-900/30';
      case 'post_reaction': return 'bg-red-50 dark:bg-red-900/30';
      case 'expert_answer': return 'bg-green-50 dark:bg-green-900/30';
      case 'new_follower': return 'bg-purple-50 dark:bg-purple-900/30';
      case 'session_reminder': return 'bg-orange-50 dark:bg-orange-900/30';
      case 'order_update': return 'bg-indigo-50 dark:bg-indigo-900/30';
      default: return 'bg-gray-50 dark:bg-gray-800';
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Mark as read
    markAsRead(notification.id);
    
    // Navigate based on notification type and action URL
    if (notification.actionUrl) {
      const url = new URL(notification.actionUrl, window.location.origin);
      const path = url.pathname;
      const params = Object.fromEntries(url.searchParams.entries());
      
      // Extract section and data from URL
      const section = path.split('/')[1] || 'hub';
      
      onNavigate(section, params);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Notifications</h2>
        <button
          onClick={() => {
            markAllAsRead();
            showToast.system.success('All notifications marked as read');
          }}
          className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 text-sm font-medium"
        >
          Mark all as read
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-base pl-9 w-full"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="flex space-x-2 overflow-x-auto scrollbar-custom">
          {[
            { id: null, label: 'All' },
            { id: 'new_message', label: 'Messages' },
            { id: 'post_reaction', label: 'Reactions' },
            { id: 'expert_answer', label: 'Expert Answers' },
            { id: 'new_follower', label: 'Followers' },
            { id: 'session_reminder', label: 'Sessions' },
            { id: 'order_update', label: 'Orders' }
          ].map((filter) => (
            <button
              key={filter.id || 'all'}
              onClick={() => setActiveFilter(filter.id)}
              className={`px-3 py-2 rounded-lg font-medium whitespace-nowrap transition-all duration-200 text-sm ${
                activeFilter === filter.id
                  ? 'bg-green-600 text-white dark:bg-green-700 dark:text-white shadow-lg'
                  : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-3">
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-green-600 dark:border-green-400 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="mx-auto text-gray-400 dark:text-gray-600 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No notifications</h3>
            <p className="text-gray-600 dark:text-gray-400">
              {searchTerm || activeFilter
                ? 'No notifications match your filters'
                : 'You have no notifications yet'}
            </p>
          </div>
        ) : (
          filteredNotifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`w-full p-4 rounded-xl text-left transition-all duration-200 hover:shadow-md ${
                notification.read 
                  ? 'bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700' 
                  : `${getNotificationColor(notification.type)} border border-gray-200 dark:border-gray-700 shadow-sm`
              }`}
            >
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 mt-1">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-1">
                  <p className={`font-medium ${notification.read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-900 dark:text-gray-100'}`}>
                    {notification.title}
                  </p>
                  <p className={`text-sm ${notification.read ? 'text-gray-600 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>
                    {notification.message}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {new Date(notification.createdAt).toLocaleString()}
                  </p>
                </div>
                {!notification.read && (
                  <div className="w-2 h-2 bg-green-600 rounded-full flex-shrink-0 mt-2"></div>
                )}
              </div>
            </button>
          ))
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;